/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { SelectOption } from "./Select.types";

export const ALL_VALUE = "all";

/**
 * Reconcile a MultiSelect selection so that the "All" sentinel and individual
 * options are mutually exclusive.
 *
 * @param previous - The selection before the toggle.
 * @param next - The raw selection produced by the underlying Select.
 * @returns The reconciled selection.
 */
export function reconcileAllExclusivity(
  previous: string[],
  next: string[],
): string[] {
  const hadAll = previous.includes(ALL_VALUE);
  const hasAll = next.includes(ALL_VALUE);

  if (hasAll && !hadAll) {
    return [ALL_VALUE];
  }

  const individuals = next.filter((value) => value !== ALL_VALUE);
  if (individuals.length > 0) {
    return individuals;
  }

  return next;
}

/**
 * Prepend the canonical "All" option to a data list.
 *
 * @param allLabel - Localized label for the "All" option.
 * @param data - The individual options.
 * @returns The option list including the "All" sentinel first.
 */
export function withAllOption(
  allLabel: string,
  data: (SelectOption | string)[],
): SelectOption[] {
  const normalized = data.map((item) =>
    typeof item === "string" ? { value: item, label: item } : item,
  );
  return [{ value: ALL_VALUE, label: allLabel }, ...normalized];
}
